import * as Sentry from '@sentry/browser'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { debounce, flowRight as compose } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { Modal } from 'components'
import { Confirmation, ModalSuccess } from 'components/Modal'

import {
  initialForm,
  schemaForm,
  create,
  formatCreate,
  BREADCRUMBS_CREATE_TICKET,
  findProductByBranchId,
  IBranchSchema,
  ISchemaFormPayload,
} from 'features/Ticket'
import {
  CreateTicketBrachInfoSection,
  CreateTicketFormSection,
  CreateTicketProductSection,
  CreateTicketSearchSection,
} from 'features/Ticket/CreateTicket'
import { getPhoneFormatter } from 'helpers/common'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import { useBreadcrumbs } from 'helpers/hooks'

import type { ISchemaForm } from 'features/Ticket'
import { ITicketSelectBranchList } from 'features/Ticket/TicketSelectBranch'
import { IParam } from 'libs/apis'
import { STATUS, TicketProductTable } from 'types'
import { useTranslation } from 'react-i18next'

export const CreateTicket: React.FC = () => {
  useBreadcrumbs(BREADCRUMBS_CREATE_TICKET)

  const history = useHistory()
  const location = useLocation<ITicketSelectBranchList>()
  const { t } = useTranslation(['common'])

  if (!location.state?.id) {
    history.push(routes.ticket.branch.path)

    return null
  }

  const base = 'con-ticket-create'
  const { handleSubmit, setValue, register, ...methods } = useForm<ISchemaForm>(
    {
      defaultValues: initialForm,
      resolver: yupResolver(schemaForm),
      mode: 'onBlur',
      reValidateMode: 'onBlur',
    },
  )

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [products, setProducts] = useState<TicketProductTable[]>([])
  const [searchParams, setSearchParams] = useState<IParam>({
    _limit: 10,
    _page: 1,
    isActive: STATUS.ACTIVE,
  })
  const [payload, setPayload] = useState<ISchemaFormPayload>()

  const handleBeforeSubmit = async (value: ISchemaForm) => {
    setIsOpenConfirm(true)

    const payload = formatCreate(value)
    setPayload(payload)
  }

  const handleConfirm = async (payload: ISchemaFormPayload) => {
    setIsLoadingBtn(true)
    setIsOpenConfirm(false)
    setIsOpenSuccess(true)

    try {
      await create(payload)
      setIsLoadingBtn(false)
      setIsError(false)

      setTimeout(() => {
        history.push(routes.ticket.path)
      }, 3000)
    } catch (error) {
      Sentry.captureException(error)
      setIsLoadingBtn(false)
      setIsError(true)
    }
  }

  useEffect(() => {
    fetchProductsByBranchId(location.state.id)
  }, [location.state.id])

  const fetchProductsByBranchId = async (branchId: string, q?: string) => {
    setIsLoading(true)
    setIsLoadingBtn(true)

    try {
      const { data } = await findProductByBranchId<TicketProductTable[]>(
        branchId,
        q,
      )

      if (data) {
        setProducts(data)
      }

      setIsLoading(false)
      setIsLoadingBtn(false)
      setIsError(false)

      const branchInfo = location.state

      const branchDetail: IBranchSchema = {
        companyId: branchInfo.companyId,
        companyName: branchInfo.companyName,
        branchId: branchInfo.id,
        branchName: branchInfo.branchName,
        branchAreaTh: branchInfo.branchAddress[0].provinceTh,
        branchAreaEn: branchInfo.branchAddress[0].provinceEn,
        branchContactName: branchInfo.branchContactName,
        branchContactPhoneNumber: branchInfo.phone
          ? getPhoneFormatter(branchInfo.phone)
          : '-',
        branchAddress: branchInfo.branchAddress[0],
      }

      register('products')
      register('branchDetail')
      setValue('branchDetail', branchDetail)
      setValue('contactName', location.state.branchContactName)
      setValue(
        'contactPhoneNumber',
        location.state.phone ? getPhoneFormatter(location.state.phone) : '',
      )
    } catch (error) {
      Sentry.captureException(error)
      setIsLoading(false)
      setIsLoadingBtn(false)
      setIsError(true)
    }
  }

  const debouncedSearch = useCallback(
    debounce((q) => {
      setValue('products', undefined)
      fetchProductsByBranchId(location.state.id, q)
    }, 1000),
    [],
  )

  return (
    <>
      <FormProvider
        {...methods}
        handleSubmit={handleSubmit}
        setValue={setValue}
        register={register}
      >
        <CreateTicketSearchSection
          baseClass={base}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          debouncedSearch={debouncedSearch}
        />

        <CreateTicketBrachInfoSection
          branchInfo={location.state}
          isLoading={isLoading}
        />

        <form className={base} onSubmit={handleSubmit(handleBeforeSubmit)}>
          <CreateTicketProductSection
            baseClass={base}
            isLoading={isLoading}
            isError={isError}
            products={products}
            setProducts={setProducts}
            setValue={setValue}
          />

          <CreateTicketFormSection
            baseClass={base}
            isLoadingBtn={isLoadingBtn}
            products={products}
          />
        </form>
      </FormProvider>

      {isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.createTicket.title')}
            description={t('common:confirmation.createTicket.description')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={() => payload && handleConfirm(payload)}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )}

      {isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.createTicket.description')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )}
    </>
  )
}

export default compose(withBackOfficeCms)(CreateTicket)
